// @flow
import React, { useEffect, useState } from 'react';
import type { Element } from 'react';

import { connect } from 'react-redux';
import { NextSeo } from 'next-seo';

import compose from 'lodash/fp/compose';
import { calculateGridUnitSize } from '@lib/helpers';
import MiniFlow from '@screens/HowItWorks/components/MiniFlow/index';
import { Link, withTranslation, nextRoutes } from '@i18n';
import { requestDoctorList } from '@screens/Finder/Doctor/redux/actions';
import { requestPackageList } from '@screens/Finder/Package/redux/actions';
import type { GlobalState } from '@globalTypes';
import { useBreakpoint } from '@hooks';
import { YAPITA_HEALTH_URL, CUREFY_URL, FINDER_PACKAGE } from '@routes';
import waitForPageReady from '@lib/waitForPageReady';
import GridCarousel from '@components/GridCarousel/index';
import PackageSearchResultListItem from '@screens/Finder/Package/SearchResultList/SearchResultListItem/index';
import SearchResultListItem from '@screens/Finder/Doctor/SearchResultList/SearchResultListItem/index';
// import FacilityForm from './FacilityForm';
import ItemsCarousel from 'react-items-carousel';
import ProgressBar from '@components/ContributionCard/ProgressBar';

import FacilityPackages from './FacilityPackages';
import type { Props, HeaderProps, DynamicMetaData } from './types';
import { requestFacilityDetails, resetFacilityDetails } from './redux/actions';


import {
  StyledFormWrapper,
  StyledBulletListWrapper,
  StyledHeadlineText,
  StyledItemContent,
  StyledCover,
  StyledBannerWrapper,
  StyledHeaderImageWrapper,
  StyledHeaderImage,
  StyledSummaryHeaderWrapper,
  StyledHeader,
  StyledBody,
  StyledOverviewData,
  StyledSection,
  StyledSectionElement,
  StyledHeaderVideo,
  StyledBannerLeftWrapper,
  StyledRichTextContent,
  StyledLocationIcon,
  StyledLocation,
  StyledFacilityBlock,
  StyledFacilityCard,
  StyledFacility,
  StyledFaciliyHeading,
  StyledFacilityInfo,
  StyledBedIcon,
  StyledDiagnoseIcon,
  StyledNABHIcon,
  StyledSpecialitiesIcon,
  StyledContentBlock,
  StyledHeading,
  StyledBookNow,
  StyledBlock,
  StyledCoverMaxWidthNoBottomMargin,
  Heading,
  StyledPackagesMobile,
  StyledFlexCenter,
  StyledSeeMore,
  StyledArrowWrapper,
  StyledBlockHospital
} from './styles';


const Header = ({
  facilityAttributes, facilityDetails, t, curefyDomain
}: HeaderProps) => {
  const photos = facilityDetails?.relationships?.photos?.data || [];
  const { locale } = nextRoutes;
  // eslint-disable-next-line max-len
  const videoUrl: ?string = facilityDetails?.relationships?.facility_group?.data?.attributes?.video_url;
  const photoUrl: string = photos.find(img => img.attributes.kind === 'header')?.attributes?.photo_url
    || '/images/finder/hospitals-banner.png';

  const textOverview = facilityAttributes?.description;

  const metaData: DynamicMetaData = t('Seo:HospitalSeo', {
    returnObjects: true,
    name: facilityAttributes?.name,
    address_city: facilityAttributes?.city,
    address_country: facilityAttributes?.address_country
  });

  return (
    <>
      <NextSeo
        title={`${facilityAttributes?.meta_title || metaData.MetaTitle} - ${
          curefyDomain ? 'Curefy' : 'Yapita Health'
        }`}
        description={facilityAttributes?.meta_description || metaData.MetaDescription}
        canonical={`${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/facility/${
          facilityAttributes?.slug
        }`}
        openGraph={{
          url: `${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/facility/${
            facilityAttributes?.slug
          }`,
          title: facilityAttributes?.meta_title || metaData.MetaTitle,
          description: facilityAttributes?.meta_description || metaData.MetaDescription,
          images: [
            {
              url:
                facilityAttributes?.seo_image.url
                || 'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: curefyDomain ? 'Curefy' : 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />
      <StyledHeader>
        <StyledBannerLeftWrapper domain={curefyDomain}>
          <StyledBannerWrapper domain={curefyDomain}>
            <StyledHeadlineText
              domain={curefyDomain}
            >
              {facilityAttributes?.name}
            </StyledHeadlineText>
            <StyledOverviewData>
              {textOverview && (
                <StyledBulletListWrapper>
                  <StyledRichTextContent dangerouslySetInnerHTML={{ __html: textOverview }} />
                </StyledBulletListWrapper>
              )}
            </StyledOverviewData>
            <StyledLocation>
              <StyledLocationIcon domain={curefyDomain} />
              {facilityAttributes?.city}
            </StyledLocation>
            <StyledBookNow domain={curefyDomain} onClick={() => window.open('/en/opinion-flow')}>
              {t('BookNow')}
            </StyledBookNow>
          </StyledBannerWrapper>
          {facilityAttributes && (
            <StyledHeaderImageWrapper>
              {videoUrl ? (
                <StyledHeaderVideo url={videoUrl} />
              ) : (
                <StyledHeaderImage alt="" src={photoUrl} />
              )}
            </StyledHeaderImageWrapper>
          )}
        </StyledBannerLeftWrapper>
      </StyledHeader>
    </>
  );
};

const FacilityDetails = ({
  resetFacilityDetails,
  facilityDetails,
  t,
  curefyDomain,
  requestPackageList,
  requestDoctorList,
  packages,
  doctors
}: Props): Element<any> => {
  const facilityAttributes = facilityDetails?.attributes;

  useEffect((): Function => resetFacilityDetails, []);
  const { amenities } = facilityAttributes || {};
  const facility_id = facilityDetails?.id;
  useEffect(() => {
    requestPackageList({ facility_id });
  }, []);
  useEffect(() => {
    requestDoctorList({ facility_id });
  }, []);

  const bedCount = facilityAttributes?.number_of_beds;
  const specialitiesCount = facilityAttributes?.specialities_count;
  const certifications = facilityAttributes?.certifications;
  const facilityName = facilityAttributes?.name;
  const cardno = bedCount && specialitiesCount && certifications ? 4 : 3;
  const isMobile: boolean = useBreakpoint('mobile', 'desktop');
  const isTablet: boolean = useBreakpoint('tablet', 'desktop');

  // eslint-disable-next-line no-nested-ternary
  const onScreenCountDoctors = isMobile ? 2 : isTablet ? 3 : 4;
  const [packageSlide, setPackageSlide] = useState(0);
  const [doctorsSlide, setDoctorsSlide] = useState(0);
  const [intervaPackagelId, setIntervalPackageId] = React.useState(null);
  const color = '#5FA6BF';
  const handlePackageTimeInterval = () => {
    clearInterval(intervaPackagelId);
    const newId = setInterval(() => {
      setPackageSlide((x) => {
        if (x > (packages || []).slice(0, 8).length - 1) {
          return 0;
        }
        return x + 1;
      });
    }, 4000);

    setIntervalPackageId(newId);
  };
  const [intervalDoctorId, setDoctorIntervalId] = React.useState(null);

  const handleDoctorTimeInterval = () => {
    clearInterval(intervalDoctorId);
    const newId = setInterval(() => {
      setDoctorsSlide((x) => {
        if (x > (doctors || []).slice(0, 8).length - 1) {
          return 0;
        }
        return x + 1;
      });
    }, 4000);

    setDoctorIntervalId(newId);
  };
  return (
  // <StyledCoverWrapper>
    <StyledCover>
      <Header
        facilityDetails={facilityDetails}
        facilityAttributes={facilityAttributes}
        t={t}
        curefyDomain={curefyDomain}
      />
      <StyledFacilityBlock>
        <StyledFaciliyHeading>{t('Facilities')}</StyledFaciliyHeading>
        {isMobile && cardno === 4 ? (
          <StyledFacility cardno={cardno}>
            <StyledBlock>
              {bedCount
                ? (
                  <StyledFacilityCard cardno={cardno}>
                    <StyledBedIcon domain={curefyDomain} />
                    <StyledFacilityInfo domain={curefyDomain}>
                      {bedCount}
                      {' '}
                      {t('beds')}
                    </StyledFacilityInfo>
                  </StyledFacilityCard>
                )
                : <></>}
              {certifications
                ? (
                  <StyledFacilityCard cardno={cardno}>
                    <StyledNABHIcon domain={curefyDomain} />
                    <StyledFacilityInfo domain={curefyDomain}>{certifications}</StyledFacilityInfo>
                  </StyledFacilityCard>
                )
                : <></>}
            </StyledBlock>
            <StyledBlock>
              {specialitiesCount
                ? (
                  <StyledFacilityCard cardno={cardno}>
                    <StyledSpecialitiesIcon domain={curefyDomain} />
                    <StyledFacilityInfo domain={curefyDomain}>
                      {'+'}
                      {specialitiesCount}
                      {' '}
                      {t('Specialities')}
                    </StyledFacilityInfo>
                  </StyledFacilityCard>
                )
                : <></>}
              <StyledFacilityCard cardno={cardno}>
                <StyledDiagnoseIcon domain={curefyDomain} />
                <StyledFacilityInfo domain={curefyDomain}>{t('Diagnostic')}</StyledFacilityInfo>
              </StyledFacilityCard>
            </StyledBlock>
          </StyledFacility>
        ) : (
          <StyledFacility cardno={cardno}>
            {bedCount
              ? (
                <StyledFacilityCard cardno={cardno}>
                  <StyledBedIcon domain={curefyDomain} />
                  <StyledFacilityInfo domain={curefyDomain}>
                    {bedCount}
                    {' '}
                    {t('beds')}
                  </StyledFacilityInfo>
                </StyledFacilityCard>
              )
              : <></>}
            {certifications
              ? (
                <StyledFacilityCard cardno={cardno}>
                  <StyledNABHIcon domain={curefyDomain} />
                  <StyledFacilityInfo domain={curefyDomain}>{certifications}</StyledFacilityInfo>
                </StyledFacilityCard>
              )
              : <></>}
            {specialitiesCount
              ? (
                <StyledFacilityCard cardno={cardno}>
                  <StyledSpecialitiesIcon domain={curefyDomain} />
                  <StyledFacilityInfo domain={curefyDomain}>
                    {'+'}
                    {specialitiesCount}
                    {' '}
                    {t('Specialities')}
                  </StyledFacilityInfo>
                </StyledFacilityCard>
              )
              : <></>}
            <StyledFacilityCard>
              <StyledDiagnoseIcon domain={curefyDomain} />
              <StyledFacilityInfo domain={curefyDomain}>{t('Diagnostic')}</StyledFacilityInfo>
            </StyledFacilityCard>
          </StyledFacility>
        )}
      </StyledFacilityBlock>
      {facilityAttributes?.description
        ? (
          <StyledBlockHospital>
            <StyledHeading domain={curefyDomain}>{t('Headlines.AboutHospital')}</StyledHeading>
            <StyledContentBlock
              domain={curefyDomain}
              dangerouslySetInnerHTML={{ __html: facilityAttributes?.description }}
            />
          </StyledBlockHospital>
          )
        : <></>}
      <StyledBody>
        <StyledSection>
          <StyledSummaryHeaderWrapper>
            {amenities
              ? (
                <StyledSectionElement>
                  <StyledHeading domain={curefyDomain}>{t('Headlines.Amenities')}</StyledHeading>
                  <StyledContentBlock
                    domain={curefyDomain}
                    dangerouslySetInnerHTML={{ __html: amenities }}
                  />
                </StyledSectionElement>
              )
              : <></>}
            <StyledItemContent>
              <StyledFormWrapper>
                <MiniFlow
                  hospital={typeof window !== 'undefined' && window?.location?.href}
                  treatmentPage="Facility"
                  formId="mobile"
                  isMobile={isMobile}
                  formName="FinderForm"
                  curefyDomain={curefyDomain}
                />
              </StyledFormWrapper>
            </StyledItemContent>
          </StyledSummaryHeaderWrapper>
        </StyledSection>
      </StyledBody>

      {packages.length <= 0 || curefyDomain
        ? <></>
        : (
          <StyledCoverMaxWidthNoBottomMargin>
            <Heading domain={curefyDomain}>
              {t('DetailsJob:TreatmentBold')}
            </Heading>
            {isMobile
              ? (
                <StyledPackagesMobile>
                  <ItemsCarousel
                    requestToChangeActive={setPackageSlide}
                    activeItemIndex={packageSlide}
                    numberOfCards={1}
                    gutter={20}
                    alwaysShowChevrons
                    showIndicators
                  >
                    {packages.slice(0, 8).map(object => (
                      <PackageSearchResultListItem
                        key={object.id}
                        size={{
                          mobile: 1
                        }}
                        result={object}
                        facilityPage
                      />
                    ))}
                  </ItemsCarousel>
                  {packages.length > 1 && (
                  <ProgressBar
                    slide={packageSlide}
                    handleTimeInterval={handlePackageTimeInterval}
                    setSlide={setPackageSlide}
                    intervalId={intervaPackagelId}
                    clickable
                    color={color}
                    homePage
                    length={(packages || []).slice(0, 8).length}
                    contributionCard
                  />
                  )}
                  <StyledFlexCenter>
                    <Link passHref href={FINDER_PACKAGE}>
                      <StyledSeeMore>{t('DetailsJob:Viewpackages')}</StyledSeeMore>
                    </Link>
                  </StyledFlexCenter>
                </StyledPackagesMobile>
              )
              : (<FacilityPackages />)}
          </StyledCoverMaxWidthNoBottomMargin>
        )}

      {doctors && doctors.length !== 0 ? (
        <StyledCoverMaxWidthNoBottomMargin>
          <StyledArrowWrapper>
            <Heading domain={curefyDomain}>{t('Doctors', { facilityName })}</Heading>
            <StyledPackagesMobile>
              {isMobile
                ? (
                  <>
                    <ItemsCarousel
                      requestToChangeActive={setDoctorsSlide}
                      activeItemIndex={doctorsSlide}
                      numberOfCards={1}
                      gutter={20}
                      alwaysShowChevrons
                      showIndicators
                    >
                      {(doctors || []).slice(0, 8).map(doctor => (
                        <SearchResultListItem
                          key={`result-list-item-${doctor.id}`}
                          result={doctor}
                          loadedOnDetails
                          bookConsultation="hide"
                          size={{
                            mobile: 1
                          }}
                          facilityPage
                        />
                      ))}
                    </ItemsCarousel>
                    {doctors.length > 1 && (
                    <ProgressBar
                      slide={doctorsSlide}
                      handleTimeInterval={handleDoctorTimeInterval}
                      setSlide={setDoctorsSlide}
                      intervalId={intervalDoctorId}
                      clickable
                      color={color}
                      homePage
                      length={(doctors || []).slice(0, 8).length}
                      contributionCard
                    />
                    )}
                  </>
                )
                : (
                  <GridCarousel
                    length={(doctors || []).slice(0, 8).length}
                    onScreenCount={onScreenCountDoctors}
                    HideArrow={(doctors || []).slice(0, 8).length / onScreenCountDoctors > 1}
                    pagination
                    homePage
                    contributionCard
                  >
                    <>
                      {(doctors || []).slice(0, 8).map(doctor => (
                        <SearchResultListItem
                          key={`result-list-item-${doctor.id}`}
                          result={doctor}
                          loadedOnDetails
                          bookConsultation="hide"
                          size={{
                            mobile: 1
                          / calculateGridUnitSize((doctors || []).slice(0, 8).length,
                            onScreenCountDoctors)
                          }}
                          facilityPage
                        />
                      ))}
                    </>
                  </GridCarousel>
                )}
            </StyledPackagesMobile>
          </StyledArrowWrapper>
        </StyledCoverMaxWidthNoBottomMargin>
      ) : (
        <></>
      )}
    </StyledCover>
  // </StyledCoverWrapper>
  );
};

FacilityDetails.getInitialProps = async ({ query, store }) => {
  store.dispatch(requestFacilityDetails(query?.slug));

  await waitForPageReady(store);
  const { getState } = store;
  const { page } = getState();

  const { curefyDomain } = page;

  return {
    namespacesRequired: ['DetailsFacility', 'Doctors', 'ContactPage'],
    query,
    curefyDomain
  };
};

const mapStateToProps = ({ facilityDetails, packageFinder, doctorFinder }: GlobalState): {} => ({
  facilityDetails: facilityDetails.data,
  packages: packageFinder?.data || [],
  doctors: doctorFinder?.data || []
});

export default compose(
  connect(
    mapStateToProps,
    {
      requestFacilityDetails, resetFacilityDetails, requestPackageList, requestDoctorList
    }
  ),
  withTranslation('DetailsFacility')
)(FacilityDetails);
